import auth from "@/middleware/auth";
import fetchUserData from "@/middleware/fetchUserData";
import isSmsVerified from "@/middleware/isSmsVerified";
import hasAnyWeightLossProgram from "@/middleware/hasAnyWeightLossProgram";
import hasActiveNotCompletedWeightLossProgram from "@/middleware/hasActiveNotCompletedWeightLossProgram";
import isClassifiedForMedication from "@/middleware/isClassifiedForMedication";
import persistWebview from "@/middleware/persistWebview";

const mainPageBreadCrumb = [{ name: "layouts.main_page", link: "Home" }];

export default {
  path: "/",
  meta: {
    middlewares: [fetchUserData, auth, isSmsVerified, persistWebview],
  },
  component: () =>
    import(/* webpackChunkName: "layout" */ "@/layouts/User.vue"),
  children: [
    {
      path: "order/:id",
      name: "OrderSummary",
      meta: {
        breadcrumb: [...mainPageBreadCrumb, { name: "layouts.cart" }],
      },
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "order-summary" */ "@/views/OrderSummary.vue"
        ),
    },
    {
      path: "order/book-collection-time/:id",
      name: "BookCollectionTime",
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "book-collection-time" */ "@/views/BookCollectionTime.vue"
        ),
    },
    {
      path: "call-me-later/referral/:id",
      name: "CallMeLaterReferral",
      meta: {
        breadcrumb: [...mainPageBreadCrumb, { name: "layouts.cart" }],
      },
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "call-me-later" */ "@/views/CallMeLater.vue"
        ),
    },
    {
      path: "call-me-later/:id",
      name: "CallMeLater",
      meta: {
        breadcrumb: [...mainPageBreadCrumb, { name: "layouts.cart" }],
      },
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "call-me-later" */ "@/views/CallMeLater.vue"
        ),
    },
    {
      path: "call-me-later/arranged/:id",
      name: "CallMeLaterArranged",
      meta: {
        breadcrumb: [...mainPageBreadCrumb, { name: "layouts.cart" }],
      },
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "call-me-later-arranged" */ "@/views/CallMeLaterArranged.vue"
        ),
    },
    {
      path: "call-me-later/referral/arranged/:id",
      name: "CallMeLaterArrangedReferral",
      meta: {
        breadcrumb: [...mainPageBreadCrumb, { name: "layouts.cart" }],
      },
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "call-me-later-arranged" */ "@/views/CallMeLaterArranged.vue"
        ),
    },

    {
      path: "dashboard",
      name: "Dashboard",
      component: () =>
        import(
          /* webpackChunkName: "dashboard" */ "@/views/User/Dashboard.vue"
        ),
    },
    {
      path: "visits",
      name: "Visits",
      component: () =>
        import(/* webpackChunkName: "visits" */ "@/views/User/Visits.vue"),
    },
    {
      path: "visits/:id",
      name: "VisitsDetails",
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "visits-details" */ "@/views/User/VisitDetails.vue"
        ),
    },
    {
      path: "doctors",
      name: "Doctors",
      component: () =>
        import(/* webpackChunkName: "doctors" */ "@/views/User/Doctors.vue"),
    },
    {
      path: "orders",
      name: "Results",
      component: () =>
        import(/* webpackChunkName: "results" */ "@/views/User/Results.vue"),
    },
    {
      path: "weight-loss/:chatId",
      name: "WeightLossChat",
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "weight-loss" */ "@/views/WeightLossChat.vue"
        ),
    },
    {
      path: "referral/:id/orders",
      name: "ReferralDetail",
      meta: {
        breadcrumb: [
          ...mainPageBreadCrumb,
          { name: "layouts.results", link: "Results" },
          { name: "layouts.order_detail" },
        ],
      },
      props: (route) => ({ id: route.params.id * 1 }),
      component: () =>
        import(
          /* webpackChunkName: "results" */ "@/views/User/ReferralDetail.vue"
        ),
    },
    {
      path: "referral/:id/results",
      name: "ReferralResults",
      meta: {
        breadcrumb: [
          ...mainPageBreadCrumb,
          { name: "layouts.results", link: "Results" },
          { name: "referrals.examination_findings" },
          { name: ":number" },
        ],
      },
      props: (route) => ({ id: route.params.id * 1 }),
      component: () =>
        import(
          /* webpackChunkName: "referral-results" */ "@/views/User/ReferralResults.vue"
        ),
    },

    {
      path: "weight-loss",
      name: "WeightLoss",
      meta: {
        middlewares: [
          fetchUserData,
          auth,
          isSmsVerified,
          hasAnyWeightLossProgram,
        ],
      },
      children: [
        {
          path: "dashboard",
          name: "WeightLoss.Dashboard",
          component: () =>
            import(
              /* webpackChunkName: "weight-loss" */ "@/views/User/WeightLoss/Dashboard.vue"
            ),
        },
        {
          path: "weights/:type",
          name: "WeightLoss.Weights",
          props: true,
          component: () =>
            import(
              /* webpackChunkName: "weight-loss" */ "@/views/User/WeightLoss/Weights.vue"
            ),
        },
        {
          path: "survey",
          name: "WeightLoss.Survey",
          component: () =>
            import(
              /* webpackChunkName: "weight-loss" */ "@/views/User/WeightLoss/Survey.vue"
            ),
        },
        {
          path: "test-results",
          name: "WeightLoss.TestResults",
          component: () =>
            import(
              /* webpackChunkName: "weight-loss" */ "@/views/User/WeightLoss/TestResults.vue"
            ),
        },
        {
          path: "prescription",
          name: "WeightLoss.Prescription",
          meta: {
            middlewares: [
              fetchUserData,
              auth,
              isSmsVerified,
              hasAnyWeightLossProgram,
              isClassifiedForMedication,
            ],
          },
          component: () =>
            import(
              /* webpackChunkName: "weight-loss" */ "@/views/User/WeightLoss/Prescription.vue"
            ),
        },
        {
          path: "training",
          name: "WeightLoss.Training",
          component: () =>
            import(
              /* webpackChunkName: "weight-loss" */ "@/views/User/WeightLoss/Training.vue"
            ),
        },
        {
          path: "dietry-plan",
          name: "WeightLoss.DietryPlan",
          component: () =>
            import(
              /* webpackChunkName: "weight-loss" */ "@/views/User/WeightLoss/DietaryPlan.vue"
            ),
        },
        {
          path: "progress",
          name: "WeightLossProgress",
          meta: {
            middlewares: [
              fetchUserData,
              auth,
              isSmsVerified,
              hasAnyWeightLossProgram,
              hasActiveNotCompletedWeightLossProgram,
            ],
          },
          component: () =>
            import(
              /* webpackChunkName: "weight-loss" */ "@/views/User/WeightLoss/Progress.vue"
            ),
        },
      ],
    },
  ],
};
